import type { HttpClient } from '~/api/client';
import { httpClient } from '~/api/client';
import type { ShippingAddress } from '~/api/model';

// Response 타입 (Zod Model 재사용 — OAISchema 금지, api-typegen §0)
type ShippingAddressResponse = ShippingAddress;

// Request DTO 직접 정의 (OAISchema 금지 — §0)
type DeliveryLocation = 'NONE' | 'INSIDE_STORE' | 'REFRIGERATOR' | 'CUSTOM';

type CreateRequest = {
  address: string;
  businessAddress?: boolean;
  businessName?: string;
  deliveryLocation?: DeliveryLocation;
  detailAddress?: string;
  items?: string[];
  provincialLevelDivisionName: string;
  recipient: string;
  recipientPhoneNumber: string;
  regionCode?: string;
  requestForDelivery?: string;
  termsId?: string;
  zipCode: string;
};

type UpdateRequest = {
  address: string;
  businessName?: string;
  deliveryLocation?: DeliveryLocation;
  detailAddress?: string;
  recipient: string;
  recipientPhoneNumber: string;
  regionCode: string;
  requestForDelivery?: string;
  shippingAddressId: number;
  zipCode: string;
};

type DeleteRequest = {
  shippingAddressId: number;
};

type UpdateDefaultRequest = {
  shippingAddressId: number;
};

export interface AddressDataSource {
  // Query
  getShippingAddresses(): Promise<ShippingAddressResponse[]>;
  getDefaultShippingAddress(): Promise<ShippingAddressResponse>;

  // Mutation
  createShippingAddress(data: CreateRequest): Promise<ShippingAddressResponse>;
  updateShippingAddress(data: UpdateRequest): Promise<ShippingAddressResponse>;
  deleteShippingAddress(data: DeleteRequest): Promise<void>;
  changeDefaultShippingAddress(data: UpdateDefaultRequest): Promise<void>;
}

export class AddressDataSourceImpl implements AddressDataSource {
  // eslint-disable-next-line no-useless-constructor
  constructor(private readonly client: HttpClient = httpClient) {}

  async getShippingAddresses() {
    return this.client.request<ShippingAddressResponse[]>({
      url: '/api/market-place/v1/shipping-address',
      method: 'get',
    });
  }

  async getDefaultShippingAddress() {
    return this.client.request<ShippingAddressResponse>({
      url: '/api/market-place/v1/shipping-address/default',
      method: 'get',
    });
  }

  async createShippingAddress(data: CreateRequest) {
    return this.client.request<ShippingAddressResponse>({
      url: '/api/market-place/v2/shipping-address',
      method: 'post',
      data,
    });
  }

  async updateShippingAddress(data: UpdateRequest) {
    return this.client.request<ShippingAddressResponse>({
      url: '/api/market-place/v1/shipping-address',
      method: 'put',
      data,
    });
  }

  async deleteShippingAddress(data: DeleteRequest) {
    await this.client.request<void>({
      url: '/api/market-place/v1/shipping-address',
      method: 'delete',
      data,
    });
  }

  async changeDefaultShippingAddress(data: UpdateDefaultRequest) {
    await this.client.request<void>({
      url: '/api/market-place/v1/shipping-address/default-change',
      method: 'patch',
      data,
    });
  }
}

export const addressDataSource = new AddressDataSourceImpl();
